import { useContentAnnounce } from "@kupikupi/api";
import type { ContentVo } from "@kupikupi/api";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";

// ─── category metadata ────────────────────────────────────────────────────────
// We use `subtitle` as the category token (the only available field in ContentVo
// that the backend uses for categorisation). BADGE_META maps well-known subtitle
// values → emoji + i18n key + badge colour. Unknown values render as plain text.

interface BadgeMeta {
  emoji: string;
  tabLabelKey: string;
  bg: string;
  urgent?: boolean;
}

const BADGE_META: Record<string, BadgeMeta> = {
  urgent: {
    emoji: "🚨",
    tabLabelKey: "tab.urgent",
    bg: "var(--color-danger)",
    urgent: true,
  },
  logistics: {
    emoji: "✈",
    tabLabelKey: "tab.logistics",
    bg: "var(--color-info)",
  },
  promo: {
    emoji: "🎁",
    tabLabelKey: "tab.promo",
    bg: "var(--color-cta)",
  },
  policy: {
    emoji: "📜",
    tabLabelKey: "tab.policy",
    bg: "var(--color-secondary)",
  },
  system: {
    emoji: "⚙",
    tabLabelKey: "tab.system",
    bg: "var(--color-primary-fill)",
  },
};

function resolveMeta(subtitle: string): BadgeMeta | null {
  return BADGE_META[subtitle.toLowerCase().trim()] ?? null;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
function formatDate(iso?: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}`;
  } catch {
    return "";
  }
}

// ─── skeleton loader ──────────────────────────────────────────────────────────
function NoticeSkeleton() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            background: "#fff",
            border: "1px solid var(--color-border)",
            borderRadius: 12,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <TgSkeleton height={20} width={60} radius={4} />
            <TgSkeleton height={12} width={32} radius={4} />
          </div>
          <TgSkeleton height={14} width="90%" />
          <TgSkeleton height={11} width="70%" />
        </div>
      ))}
    </>
  );
}

// ─── badge pill ───────────────────────────────────────────────────────────────
function BadgePill({ subtitle, t }: { subtitle: string; t: (k: string) => string }) {
  const meta = resolveMeta(subtitle);
  const bg = meta?.bg ?? "var(--color-text-muted)";
  const label = meta ? `${meta.emoji} ${t(meta.tabLabelKey)}` : subtitle;

  return (
    <span
      style={{
        padding: "3px 10px",
        background: bg,
        color: "#fff",
        fontFamily: "monospace",
        fontSize: 9,
        fontWeight: 800,
        borderRadius: 4,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── notice card ──────────────────────────────────────────────────────────────
function NoticeCard({ item, t }: { item: ContentVo; t: (k: string) => string }) {
  const meta = resolveMeta(item.subtitle);
  const isUrgent = meta?.urgent === true;
  const dateStr = formatDate(item.createTime);
  const body = (item.content || item.description || "").replace(/<[^>]+>/g, "");

  const headerRow = (
    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
      {item.subtitle && <BadgePill subtitle={item.subtitle} t={t} />}
      {dateStr && (
        <span
          style={{
            fontFamily: "monospace",
            fontSize: 9,
            color: "var(--color-text-muted)",
            marginLeft: "auto",
          }}
        >
          {dateStr}
        </span>
      )}
    </div>
  );

  const titleEl = (
    <h2
      style={{
        fontFamily: "-apple-system,system-ui,sans-serif",
        fontSize: isUrgent ? 14 : 13,
        fontWeight: 800,
        margin: "0 0 4px",
        lineHeight: 1.3,
      }}
    >
      {item.title}
    </h2>
  );

  const bodyEl = body ? (
    <p
      style={{
        fontSize: 11,
        color: "var(--color-text-muted)",
        margin: 0,
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
      }}
    >
      {body}
    </p>
  ) : null;

  if (isUrgent) {
    return (
      <article
        style={{
          background: "linear-gradient(135deg,var(--color-danger-soft),#fff)",
          border: "1.5px solid var(--color-danger)",
          borderRadius: 12,
          padding: 14,
        }}
      >
        {headerRow}
        {titleEl}
        {bodyEl}
      </article>
    );
  }

  return (
    <article
      style={{
        background: "#fff",
        border: "1px solid var(--color-border)",
        borderRadius: 12,
        padding: 12,
      }}
    >
      {headerRow}
      {titleEl}
      {bodyEl}
    </article>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────
export function NoticePage() {
  const { t } = useTranslation("notice");
  const { data, isLoading } = useContentAnnounce();
  const items: ContentVo[] = data ?? [];

  // Build unique subtitle list (= category tokens), urgent first
  const subtitleOrder = Array.from(
    new Set(
      [...items]
        .sort((a, b) => {
          const aU = resolveMeta(a.subtitle)?.urgent ? 0 : 1;
          const bU = resolveMeta(b.subtitle)?.urgent ? 0 : 1;
          return aU - bU;
        })
        .map((it) => it.subtitle)
        .filter(Boolean),
    ),
  );

  const [activeTab, setActiveTab] = useState<string>("all");

  const filtered =
    activeTab === "all" ? items : items.filter((it) => it.subtitle === activeTab);

  function countFor(sub: string) {
    return items.filter((it) => it.subtitle === sub).length;
  }

  const showTabs = !isLoading && subtitleOrder.length > 0;

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      {/* ── category tab bar ── */}
      {showTabs && (
        <div
          style={{
            display: "flex",
            gap: 4,
            padding: "8px 12px",
            background: "#fff",
            borderBottom: "1px solid var(--color-border)",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            style={{
              background: activeTab === "all" ? "var(--color-text)" : "transparent",
              color: activeTab === "all" ? "#fff" : "var(--color-text-muted)",
              padding: "5px 10px",
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 700,
              border: 0,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {t("tab.all")} {items.length}
          </button>

          {subtitleOrder.map((sub) => {
            const meta = resolveMeta(sub);
            const label = meta ? `${meta.emoji} ${t(meta.tabLabelKey)}` : sub;
            const isActive = activeTab === sub;
            return (
              <button
                key={sub}
                type="button"
                onClick={() => setActiveTab(sub)}
                style={{
                  background: isActive ? "var(--color-text)" : "transparent",
                  color: isActive ? "#fff" : "var(--color-text-muted)",
                  padding: "5px 10px",
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  border: 0,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                {label} {countFor(sub)}
              </button>
            );
          })}
        </div>
      )}

      {/* ── body ── */}
      <div
        className="tg-body"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "10px 12px",
          overflowY: "auto",
        }}
      >
        {isLoading && <NoticeSkeleton />}

        {!isLoading && filtered.length === 0 && (
          <TgEmpty icon="📢" title={t("empty.title")} hint={t("empty.hint")} />
        )}

        {!isLoading && filtered.map((it) => <NoticeCard key={it.id} item={it} t={t} />)}
      </div>
    </TgPage>
  );
}
